import {
  ChevronLeft,
  ChevronRight,
  Favorite,
  LocationOn,
  Lock,
  Person,
  Share,
  ShoppingCart,
  Support,
} from "@mui/icons-material";
import {
  Box,
  Container,
  Grid,
  IconButton,
  Paper,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";

import ProfileInfo from "../../components/frontend/profile/ProfileInfo";
import ProfileSidebar from "../../components/frontend/profile/ProfileSidebar";
import { useAuthStore } from "../../store/useAuthStore";
import "../../styles/profile.css";
import { ProfileSection } from "../../types/user";

const ProfilePage: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuthStore();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [activeSection, setActiveSection] =
    useState<ProfileSection>("personal");
  const [scrollPosition, setScrollPosition] = useState(0);
  const [visibleTabsCount, setVisibleTabsCount] = useState(4); // Default to 4 tabs

  const handleSectionChange = (section: ProfileSection) => {
    setActiveSection(section);
  };

  const handleScrollLeft = () => {
    const newPosition = Math.max(0, scrollPosition - visibleTabsCount);
    setScrollPosition(newPosition);
  };

  const handleScrollRight = () => {
    const maxPosition = Math.max(0, mobileNavItems.length - visibleTabsCount);
    const newPosition = Math.min(
      maxPosition,
      scrollPosition + visibleTabsCount,
    );
    setScrollPosition(newPosition);
  };

  // Calculate how many tabs can fit based on screen width
  React.useEffect(() => {
    const calculateVisibleTabs = () => {
      const screenWidth = window.innerWidth;
      let newCount;

      if (screenWidth < 400) {
        newCount = 3;
      } else if (screenWidth < 500) {
        newCount = 4;
      } else if (screenWidth < 600) {
        newCount = 5;
      } else {
        newCount = 6;
      }

      // Only update if the count actually changed
      if (newCount !== visibleTabsCount) {
        setVisibleTabsCount(newCount);
        // Reset scroll position when tab count changes
        setScrollPosition(0);
      }
    };

    calculateVisibleTabs();
    window.addEventListener("resize", calculateVisibleTabs);
    return () => window.removeEventListener("resize", calculateVisibleTabs);
  }, [visibleTabsCount]);

  React.useEffect(() => {
    const initialSection = (
      location.state as { initialSection?: ProfileSection } | null
    )?.initialSection;
    if (initialSection) {
      setActiveSection(initialSection);
    }
  }, [location.state]);

  // Mobile navigation items - same as desktop sidebar
  const mobileNavItems = [
    {
      id: "personal" as ProfileSection,
      label: t("profile.personalInfo"),
      icon: <Person />,
    },
    {
      id: "orders" as ProfileSection,
      label: t("profile.orders"),
      icon: <ShoppingCart />,
    },
    {
      id: "wishlist" as ProfileSection,
      label: t("profile.wishlist"),
      icon: <Favorite />,
    },
    {
      id: "address" as ProfileSection,
      label: t("profile.address"),
      icon: <LocationOn />,
    },
    {
      id: "changePassword" as ProfileSection,
      label: t("profile.changePassword"),
      icon: <Lock />,
    },
    {
      id: "referral" as ProfileSection,
      label: t("profile.referral.title"),
      icon: <Share />,
    },
    {
      id: "support" as ProfileSection,
      label: t("profile.supportTicket"),
      icon: <Support />,
    },
  ];

  const renderMainContent = () => {
    switch (activeSection) {
      case "personal":
        return <ProfileInfo user={user} section="personal" />;
      case "orders":
        return <ProfileInfo user={user} section="orders" />;
      case "wishlist":
        return <ProfileInfo user={user} section="wishlist" />;
      case "address":
        return <ProfileInfo user={user} section="address" />;
      case "changePassword":
        return <ProfileInfo user={user} section="changePassword" />;
      case "support":
        return <ProfileInfo user={user} section="support" />;
      case "referral":
        return <ProfileInfo user={user} section="referral" />;
      default:
        return <ProfileInfo user={user} section="personal" />;
    }
  };

  return (
    <Container
      maxWidth="xl"
      disableGutters
      sx={{ p: 0, backgroundColor: "white" }}
    >
      {/* Mobile Horizontal Scrolling Navigation */}
      {isMobile && (
        <Box
          sx={{
            backgroundColor: "#ffffff",
            borderBottom: "1px solid #e5e7eb",
            px: 2,
            py: 1,
            position: "relative",
          }}
        >
          {/* Left Scroll Button */}
          {scrollPosition > 0 && (
            <IconButton
              onClick={handleScrollLeft}
              sx={{
                position: "absolute",
                left: 8,
                top: "50%",
                transform: "translateY(-50%)",
                zIndex: 2,
                backgroundColor: "rgba(255, 255, 255, 0.9)",
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                "&:hover": {
                  backgroundColor: "rgba(255, 255, 255, 1)",
                },
              }}
            >
              <ChevronLeft />
            </IconButton>
          )}

          {/* Right Scroll Button */}
          {scrollPosition < mobileNavItems.length - visibleTabsCount && (
            <IconButton
              onClick={handleScrollRight}
              sx={{
                position: "absolute",
                right: 8,
                top: "50%",
                transform: "translateY(-50%)",
                zIndex: 2,
                backgroundColor: "rgba(255, 255, 255, 0.9)",
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                "&:hover": {
                  backgroundColor: "rgba(255, 255, 255, 1)",
                },
              }}
            >
              <ChevronRight />
            </IconButton>
          )}

          {/* Navigation Tabs */}
          <Box
            sx={{
              display: "flex",
              overflow: "hidden",
              gap: 1,
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            {mobileNavItems
              .slice(scrollPosition, scrollPosition + visibleTabsCount)
              .map((item) => (
                <Box
                  key={item.id}
                  onClick={() => handleSectionChange(item.id)}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    flex: 1,
                    minWidth: "60px",
                    maxWidth: "100px",
                    py: 1,
                    px: 0.5,
                    cursor: "pointer",
                    borderRadius: "8px",
                    backgroundColor:
                      activeSection === item.id ? "#e3f2fd" : "transparent",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      backgroundColor:
                        activeSection === item.id ? "#e3f2fd" : "#f5f5f5",
                    },
                  }}
                >
                  <Box
                    sx={{
                      color: activeSection === item.id ? "#2563eb" : "#6b7280",
                      mb: 0.5,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {item.icon}
                  </Box>
                  <Typography
                    variant="caption"
                    className="notranslate"
                    translate="no"
                    sx={{
                      fontSize: "0.7rem",
                      fontWeight: activeSection === item.id ? 600 : 500,
                      color: activeSection === item.id ? "#2563eb" : "#6b7280",
                      textAlign: "center",
                      lineHeight: 1.2,
                    }}
                  >
                    {item.label}
                  </Typography>
                </Box>
              ))}
          </Box>
        </Box>
      )}

      {/* Main Content Container - Full Width */}
      <Box sx={{ width: "100%", margin: 0, padding: 0 }}>
        <Paper
          elevation={0}
          sx={{
            borderRadius: 0,
            backgroundColor: "#ffffff",
            overflow: "hidden",
            mt: 0,
            mb: 0,
            border: "none",
            boxShadow: "none",
            width: "100%",
            margin: 0,
            padding: 0,
          }}
        >
          <Grid container spacing={0} sx={{ height: "100%" }}>
            {/* Desktop Sidebar */}
            {!isMobile && (
              <Grid item xs={12} md={3}>
                <ProfileSidebar
                  activeSection={activeSection}
                  onSectionChange={handleSectionChange}
                />
              </Grid>
            )}

            {/* Main Content */}
            <Grid item xs={12} md={9}>
              <Box sx={{ p: { xs: 1, sm: 2 }, height: "100%" }}>
                {renderMainContent()}
              </Box>
            </Grid>
          </Grid>
        </Paper>
      </Box>
    </Container>
  );
};

export default ProfilePage;
